import { useState } from 'react';

import ProductUnitPopup from './ProductUnitPopup';
import EditProductUnitPopup from './EditProductUnitPopup';
import { useProductUnits } from '../../../hooks/useProductUnits';
import BasicDataTable from '../../DataTable/BasicDataTable';
import BasicSearchComponent from '../../DataTable/BasicSearchComponent';
import ActivationModal from '../../Modals/ActivationModal';
import { IProductUnitCreate, IProductUnitSelected } from '../../../types/productUnit/productUnitInterfaces';

const ProductUnitTable = () => {
  const {
    productUnits,
    handlerSearchByType,
    toggleProductUnitStatus,
    handlerAddProductUnit,
  } = useProductUnits();

  // Estados para los modales
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<IProductUnitSelected | null>(null);
  const [isUnitPopupOpen, setIsUnitPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);

  // 📌 Función para manejar el registro de unidades de medida
  const handleRegisterUnit = (unitData: IProductUnitCreate) => {
    console.log('Unidad de medida registrada:', unitData);
    handlerAddProductUnit(unitData);
  };

  // 📌 Función para manejar la edición de unidades de medida
  const handleUpdateUnit = (unitData: IProductUnitCreate) => {
    console.log('Unidad de medida actualizada:', unitData);
    handlerAddProductUnit(unitData);
    // Aquí puedes llamar a la API para actualizar la unidad de medida
  };

  // 📌 Función para abrir el modal de edición con la unidad seleccionada
  const handleEditUnit = (unit: IProductUnitSelected) => {
    console.log('Editando unidad de medida:', unit);
    setSelectedUnit(unit);
    setIsEditPopupOpen(true);
  };

  // 📌 Columnas de la tabla
  const columns = [
    {
      Header: 'Nombre',
      accessor: 'name',
    },
    {
      Header: 'Código Unidad',
      accessor: 'unitCode',
      Cell: ({ value }) => (
        <span className="text-gray-700 font-medium">{value}</span>
      ),
    },
    {
      Header: 'Código MH',
      accessor: 'mhCode',
      Cell: ({ value }) => (
        <span className="font-mono font-semibold text-blue-600">
          {String(value).padStart(2, '0')}
        </span>
      ),
    },
    {
      Header: 'Estado',
      accessor: 'status',
      Cell: ({ value }) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
            value ? 'bg-green-500 text-white' : 'bg-red text-white'
          }`}
        >
          {value ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
  ];

  // 📌 Botón para abrir el modal
  const addButton = (
    <button
      className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-white hover:bg-opacity-90"
      onClick={() => setIsUnitPopupOpen(true)}
    >
      + Unidad de Medida
    </button>
  );

  // 📌 Función para manejar la activación/desactivación de la unidad de medida
  const handleToggleUnitStatus = (unit: IProductUnitSelected) => {
    setSelectedUnit(unit);
    setIsActivationModalOpen(true);
  };

  // 📌 Confirmar activación/desactivación
  const handleConfirmToggleStatus = () => {
    if (selectedUnit) {
      toggleProductUnitStatus(selectedUnit.id);
    }
    setIsActivationModalOpen(false);
  };

  // 📌 Cancelar activación/desactivación
  const handleCancelToggle = () => {
    setIsActivationModalOpen(false);
    setSelectedUnit(null);
  };

  // 📌 Acciones para la tabla
  const actions = [
    { key: 'edit', onClick: handleEditUnit }, // 🔹 Ahora llama `handleEditUnit` correctamente
    { key: 'toggle', onClick: handleToggleUnitStatus },
  ];

  return (
    <>
      <BasicDataTable
        columns={columns}
        data={productUnits}
        noDataMessage="No hay unidades de medida disponibles."
        actions={actions}
        headerButton={addButton}
        searchComponent={
          <BasicSearchComponent onSearch={handlerSearchByType} />
        }
      />

      {/* Modal para activar/desactivar unidad de medida */}
      <ActivationModal
        isOpen={isActivationModalOpen}
        title={
          selectedUnit?.status
            ? '¿Desactivar Unidad de Medida?'
            : '¿Activar Unidad de Medida?'
        }
        message={
          selectedUnit?.status
            ? '¿Estás seguro de que deseas desactivar esta unidad de medida?'
            : '¿Estás seguro de que deseas activar esta unidad de medida?'
        }
        confirmText={selectedUnit?.status ? 'Desactivar' : 'Activar'}
        cancelText="Cancelar"
        onConfirm={handleConfirmToggleStatus}
        onCancel={handleCancelToggle}
      />

      {/* Modal para agregar unidad de medida */}
      <ProductUnitPopup
        popupOpen={isUnitPopupOpen}
        setPopupOpen={setIsUnitPopupOpen}
        onRegister={handleRegisterUnit}
      />

      {/* Modal para editar unidad de medida */}
      <EditProductUnitPopup
        popupOpen={isEditPopupOpen}
        setPopupOpen={setIsEditPopupOpen}
        onUpdate={handleUpdateUnit}
        selectedUnit={selectedUnit}
      />
    </>
  );
};

export default ProductUnitTable;
